/**
 * Centralized Deal Cache Service
 *
 * Industry Standard Pattern (used by Facebook/Relay, Airbnb, Uber):
 * - Single source of truth for deal entities
 * - Update once → reflects everywhere automatically
 * - No manual cache synchronization across queries
 *
 * This avoids the React Query anti-pattern of duplicating data across
 * multiple query keys and manually keeping them in sync.
 */

import { QueryClient } from '@tanstack/react-query';
import { devLog } from '@/utils/devLog';
import type { Deal, StageConfig } from '@/types/dealflow';

// Deal entity type (minimal fields needed for cache updates)
export interface DealEntity {
  id: string;
  stageId?: string;
  fieldValues?: Record<string, string | null>;
  [key: string]: unknown;
}

// Cache structure type for deal flow data
interface DealFlowCache {
  deals: Deal[];
  stages: StageConfig[];
  organization: string;
}

// --- Recent Mutation Tracking ---
// Prevents socket echo from overwriting optimistic updates
const MUTATION_TTL_MS = 3000;
const recentMutations = new Map<string, number>();

export function registerRecentMutation(dealId: string): void {
  recentMutations.set(dealId, Date.now());
}

export function isRecentMutation(dealId: string): boolean {
  const timestamp = recentMutations.get(dealId);
  if (!timestamp) return false;
  if (Date.now() - timestamp > MUTATION_TTL_MS) {
    recentMutations.delete(dealId);
    return false;
  }
  return true;
}

/**
 * Updates a deal entity across ALL cached queries
 *
 * This is the Facebook/Relay pattern: update the entity once,
 * and it propagates to all queries that contain it.
 */
export function updateDealInAllCaches(
  queryClient: QueryClient,
  dealId: string,
  updates: Partial<DealEntity>
): void {
  devLog('[DealCache] Updating deal across all caches:', dealId, updates);

  // 1. Update in all dealflow queries (user and organization variants)
  // Use predicate to match all ['dealflow', 'user'] and ['dealflow', 'organization', *] queries
  queryClient.setQueriesData<DealFlowCache>(
    {
      predicate: (query) => {
        const key = query.queryKey;
        if (!Array.isArray(key) || key[0] !== 'dealflow') return false;
        // Match ['dealflow', 'user'] or ['dealflow', 'organization', <orgId>]
        return key[1] === 'user' || key[1] === 'organization';
      }
    },
    (oldData) => {
      if (!oldData || !oldData.deals || !Array.isArray(oldData.deals)) return oldData;

      // Handle fieldValues update specially to merge with existing values
      const updatedDeals = oldData.deals.map((deal) => {
        if (deal.id !== dealId) return deal;

        // If updating fieldValues, merge with existing
        if (updates.fieldValues) {
          return {
            ...deal,
            ...updates,
            fieldValues: {
              ...(deal.fieldValues || {}),
              ...Object.fromEntries(
                Object.entries(updates.fieldValues).filter(([, v]) => v !== null)
              ),
            },
          };
        }

        return { ...deal, ...updates };
      });

      return {
        ...oldData,
        deals: updatedDeals,
      };
    }
  );

  // 2. Update specific deal detail cache if it exists ['dealflow', 'deal', dealId]
  queryClient.setQueryData<Deal>(
    ['dealflow', 'deal', dealId],
    (oldData) => {
      if (!oldData) return oldData;

      // Handle fieldValues update specially to merge with existing values
      if (updates.fieldValues) {
        return {
          ...oldData,
          ...updates,
          fieldValues: {
            ...(oldData.fieldValues || {}),
            ...Object.fromEntries(
              Object.entries(updates.fieldValues).filter(([, v]) => v !== null)
            ),
          },
        } as Deal;
      }

      return { ...oldData, ...updates } as Deal;
    }
  );
}

/**
 * Removes a deal from ALL cached queries
 */
export function removeDealFromAllCaches(
  queryClient: QueryClient,
  dealId: string
): void {
  devLog('[DealCache] Removing deal from all caches:', dealId);

  // 1. Remove from all dealflow queries (user and organization variants)
  queryClient.setQueriesData<DealFlowCache>(
    {
      predicate: (query) => {
        const key = query.queryKey;
        if (!Array.isArray(key) || key[0] !== 'dealflow') return false;
        return key[1] === 'user' || key[1] === 'organization';
      }
    },
    (oldData) => {
      if (!oldData || !oldData.deals || !Array.isArray(oldData.deals)) return oldData;
      return {
        ...oldData,
        deals: oldData.deals.filter((deal) => deal.id !== dealId),
      };
    }
  );

  // 2. Remove specific deal detail cache
  queryClient.removeQueries({ queryKey: ['dealflow', 'deal', dealId] });
}

/**
 * Invalidates only aggregate/stats queries that need server refresh
 * Used after mutations when the entity cache is already up-to-date
 *
 * This is minimal - we don't invalidate deal list queries because
 * they were already updated directly via updateDealInAllCaches
 */
export function invalidateDealFlowAggregates(queryClient: QueryClient): void {
  // Only invalidate statistics/aggregates that can't be computed client-side
  queryClient.invalidateQueries({ queryKey: ['dealflow', 'stats'] });
  queryClient.invalidateQueries({ queryKey: ['dealflow', 'analytics'] });
}

/**
 * Gets the appropriate query key for a deal flow operation
 */
export function getDealFlowQueryKey(organizationId?: string): string[] {
  return organizationId
    ? ['dealflow', 'organization', organizationId]
    : ['dealflow', 'user'];
}

/**
 * Snapshot interface for rollback capability
 */
export interface DealCacheSnapshot {
  queryKey: string[];
  previousData: DealFlowCache | undefined;
  dealId: string;
}

/**
 * Creates a snapshot of deal data for rollback purposes
 */
export function createDealCacheSnapshot(
  queryClient: QueryClient,
  dealId: string,
  organizationId?: string
): DealCacheSnapshot {
  const queryKey = getDealFlowQueryKey(organizationId);
  const previousData = queryClient.getQueryData<DealFlowCache>(queryKey);

  return {
    queryKey,
    previousData,
    dealId,
  };
}

/**
 * Restores deal data from a snapshot (for rollback on error)
 */
export function restoreDealCacheSnapshot(
  queryClient: QueryClient,
  snapshot: DealCacheSnapshot
): void {
  if (snapshot.previousData) {
    queryClient.setQueryData(snapshot.queryKey, snapshot.previousData);
  }
}
